import {
  Users, Building2, TrendingUp, FileText 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { Menu } from '@/features/menus';
import { getMenuTypeStyle } from './constants';

interface ForecastViewProps {
  readonly menus: Menu[];
  readonly stats: {
    readonly totalPax: number;
    readonly totalCapacity: number;
    readonly utilization: number;
    readonly active: number;
  };
  readonly forecastPax: Record<string, number>;
  readonly handlePaxChange: (menuId: string, date: string, value: number) => void;
}

export function ForecastView({
  menus,
  stats,
  forecastPax,
  handlePaxChange,
}: ForecastViewProps) {
  const activeMenus = menus.filter((m) => m.status === 'active');
  const dateStr = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Expected Pax</CardTitle>
            <Users className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.totalPax.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Daily average</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Capacity</CardTitle>
            <Building2 className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.totalCapacity.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">All venues</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Utilization</CardTitle>
            <TrendingUp className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.utilization}%</div>
            <p className="text-xs text-muted-foreground">Capacity usage</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Menus</CardTitle>
            <FileText className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pax Forecast by Menu</CardTitle>
          <p className="text-sm text-muted-foreground">Adjust expected guest counts to forecast ingredient demand</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {activeMenus.map((menu) => {
              const schedule = menu.schedule?.find((s) => s.date === dateStr);
              const currentPax = forecastPax[`${menu.menuId}-${dateStr}`] ?? schedule?.expectedPax ?? menu.defaultPax ?? 0;
              const utilization = menu.venueCapacity ? Math.round((currentPax / menu.venueCapacity) * 100) : 0;

              return (
                <div key={menu.menuId} className="flex items-center gap-4 rounded-lg border p-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${getMenuTypeStyle(menu.type)}`}>
                        {menu.type}
                      </span>
                      <span className="font-medium">{menu.name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{menu.venueName}</p>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Capacity</p>
                      <p className="font-medium">{menu.venueCapacity?.toLocaleString() ?? '-'}</p>
                    </div>

                    <div className="w-48">
                      <label className="text-xs text-muted-foreground">Expected Pax</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={currentPax}
                          onChange={(e) => handlePaxChange(menu.menuId, dateStr, parseInt(e.target.value) || 0)}
                          className="w-24 rounded-md border bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          min={0}
                          max={menu.venueCapacity ?? 9999}
                        />
                        <span className="text-sm text-muted-foreground">
                          ({utilization}%)
                        </span>
                      </div>
                    </div>

                    <Button size="sm" variant="outline">
                      <TrendingUp className="mr-2 h-4 w-4" />
                      Forecast
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ingredient Demand Preview</CardTitle>
          <p className="text-sm text-muted-foreground">Based on current pax forecast and recipe requirements</p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <TrendingUp className="mx-auto h-12 w-12 mb-4" />
            <p>Adjust pax values above and click "Forecast" to calculate ingredient demand</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
